import { basePersistentApi } from '../baseApi';
import { gql } from 'graphql-request';
import { authApi } from './authApi';

export const userContextApi = basePersistentApi.injectEndpoints({
  endpoints: (builder: any) => ({
    // queries
    getUserContext: builder.query({
      query: () => ({
        body: gql`
          query GetUserContext {
            getUserContext {
              id
              username
              email
              is_institution
              is_verified
              is_active
              user_profile {
                id
                first_name
                last_name
                date_of_birth
                is_real_birthdate
                primary_email
                secondary_email
                mobile_number
                affiliation
                state
                postcode
              }
              institution_profile {
                id
                institution_name
                primary_email
                address_line1
                address_line2
                city
                state
                postcode
                phone_number
                contact_person {
                  name
                  email
                  mobile_number
                }
              }
              roles {
                id
                role {
                  name
                  short_code
                  id
                  allowed_subsites {
                    short_code
                    name
                    url
                  }
                }
                assigned_by {
                  id
                  username
                  email
                  is_institution
                  user_profile {
                    id
                    first_name
                    last_name
                  }
                  institution_profile {
                    institution_name
                  }
                }
              }
              licenses {
                id
                is_assigned
                is_renewed
                date_assigned
                assigned_by
                assignee_email
                licence_assignee {
                  id
                  email
                  is_institution
                  user_profile {
                    first_name
                    last_name
                  }
                  institution_profile {
                    institution_name
                  }
                }
              }
              user_resource_cart {
                id
                element_group_resource_id
                element_group_resource {
                  id
                  name
                  description
                  term
                  pricing
                  grade_level
                  status
                  concept_zone_id
                  border_color
                  duration
                  price
                }
              }
            }
          }
        `,
      }),
      transformResponse: (res: any) => res.getUserContext,
      async onQueryStarted(
        _: unknown,
        {
          dispatch,
          queryFulfilled
        }: {
          dispatch: any;
          queryFulfilled: Promise<any>;
          }
      ) {
        try {
          await queryFulfilled;
        } catch (err: any) {
          const message = err?.error?.data?.response?.errors[0]?.message;

          if (message === "Invalid session.") {
            try {
              await dispatch(authApi.endpoints.removeCookie.initiate('')).unwrap();
              const redirectUrl = import.meta.env.VITE_MAIN_SITE_URL + "/login/redirect/DVR";
              window.location.href = redirectUrl;
            } catch (logoutErr) {
              console.warn("Logout request failed:", logoutErr);
            }
          }
        }
      },
    }),
    addResourceToCart: builder.mutation({
      query: ({
        useraccount_id,
        element_group_resource_id,
        is_direct_buying,
      }: any) => ({
        body: gql`
          mutation Mutation($input: UserCreateCartInput) {
            createUserResourceCart(input: $input) {
              id
              element_group_resource_id
              element_group_resource {
                id
                name
                description
                term
                pricing
                grade_level
                status
                concept_zone_id
                border_color
                duration
                price
              }
            }
          }
        `,
        variables: {
          input: {
            useraccount_id,
            element_group_resource_id,
            is_direct_buying,
          },
        },
      }),
      transformResponse: (res: any) => res.createUserResourceCart,
    }),
    addResourceToWishlist: builder.mutation({
      query: ({
        useraccount_id,
        element_group_resource_id,
      }: any) => ({
        body: gql`
          mutation Mutation($input: UserCreateWishlistInput) {
            createUserResourceWishlist(input: $input) {
              id
              element_group_resource_id
              element_group_resource {
                id
                name
                description
                term
                pricing
                grade_level
                status
                concept_zone_id
                border_color
                duration
                price
              }
            }
          }
        `,
        variables: {
          input: {
            useraccount_id,
            element_group_resource_id,
          },
        },
      }),
      transformResponse: (res: any) => res.createUserResourceWishlist,
    }),
    deleteResourceCart: builder.mutation({
      query: (id: any) => ({
        body: gql`
          mutation Mutation($id: ID!) {
            deleteResourceCart(id: $id)
          }
        `,
        variables: {
          id,
        },
      }),
      transformResponse: (res: any) => res.deleteResourceCart,
    }),
    deleteResourceWishlist: builder.mutation({
      query: (id: any) => ({
        body: gql`
          mutation Mutation($id: ID!) {
            deleteResourceWishlist(id: $id)
          }
        `,
        variables: {
          id,
        },
      }),
      transformResponse: (res: any) => res.deleteResourceWishlist,
    }),
    resourceCheckout: builder.mutation({
      query: ({
        useraccount_id,
        user_resource_cart_ids,
      }: any) => ({
        body: gql`
          mutation Mutation($input: UserCreateCheckoutInput) {
            createUserResourceCheckout(input: $input)
          }
        `,
        variables: {
          input: {
            useraccount_id,
            user_resource_cart_ids,
          },
        },
      }),
      transformResponse: (res: any) => res.createUserResourceCheckout,
    }),
    getResourceWishlistDetails: builder.query({
      query: (id: any) => ({
        body: gql`
          query Query($element_group_resource_id: ID!) {
            findUserResourceWishlistElementGroupResourceId(element_group_resource_id: $element_group_resource_id) {
              id
              element_group_resource_id
              element_group_resource {
                id
                name
                description
                term
                pricing
                grade_level
                status
                concept_zone_id
                border_color
                duration
                price
                resource_image {
                  url
                  resource_type
                }
              }
            }
          }
        `,
        variables: {
          element_group_resource_id: id,
        },
      }),
      transformResponse: (res: any) => res.findUserResourceWishlistElementGroupResourceId,
    }),
    getResourcePurchaseActiveDetails: builder.query({
      query: ({ id, access = 0} : any) => ({
        body: gql`
          query Query($element_group_resource_id: ID!, $access: Int) {
            findUserActiveResourcePurchaseByElementGroupResourceId(element_group_resource_id: $element_group_resource_id, access: $access) {
              id
              element_group_resource_id
              newly_accessed
              element_group_resource {
                id
                name
                description
                term
                pricing
                grade_level
                status
                concept_zone_id
                border_color
                duration
                price
                resource_image {
                  url
                  resource_type
                }
              }
            }
          }
        `,
        variables: {
          element_group_resource_id: id,
          access,
        },
      }),
      transformResponse: (res: any) => res.findUserActiveResourcePurchaseByElementGroupResourceId,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUserContextQuery,
  useLazyGetUserContextQuery,
  useAddResourceToCartMutation,
  useAddResourceToWishlistMutation,
  useDeleteResourceCartMutation,
  useDeleteResourceWishlistMutation,
  useResourceCheckoutMutation,
  useLazyGetResourcePurchaseActiveDetailsQuery,
  useLazyGetResourceWishlistDetailsQuery,
} = userContextApi;

// @ts-ignore
export const useGetUserContext = userContextApi.endpoints.getUserContext.useQueryState;
